export enum Locale {
  EN = 'en',
  KO = 'ko',
  JA = 'ja',
  ZH_CN = 'zh-CN',
  ZH_TW = 'zh-TW',
  RU = 'ru',
  PL = 'pl',
  CS = 'cs',
  FR = 'fr',
  DE = 'de',
  NL = 'nl'
}

/**
 * Dev-only pseudo-locale: every translated string is accented and padded, so text
 * that does not go through `t()` (still plain English) and layouts that cannot take
 * longer text stand out. Never offered in production builds.
 */
export const PSEUDO_LOCALE = 'en-XA'

export type AppLocale = Locale | typeof PSEUDO_LOCALE

/** What the user picked in Settings → Language. `system` follows the device language. */
export type LocalePreference = AppLocale | 'system'

export const SYSTEM_LOCALE_PREFERENCE = 'system'

/**
 * Storage key for the language preference. The same key is used in the page's
 * sync storage (localStorage / MMKV) and, on the extension, in
 * `browser.storage.local` so the background service worker can read it.
 */
export const LOCALE_STORAGE_KEY = 'locale'

export const DEFAULT_LOCALE = Locale.EN

/** Language names written in the language itself, as shown in the language picker. */
export const LOCALE_NATIVE_NAMES: Record<AppLocale, string> = {
  [Locale.EN]: 'English',
  [Locale.KO]: '한국어',
  [Locale.JA]: '日本語',
  [Locale.ZH_CN]: '简体中文',
  [Locale.ZH_TW]: '繁體中文',
  [Locale.RU]: 'Русский',
  [Locale.PL]: 'Polski',
  [Locale.CS]: 'Čeština',
  [Locale.FR]: 'Français',
  [Locale.DE]: 'Deutsch',
  [Locale.NL]: 'Nederlands',
  [PSEUDO_LOCALE]: 'Pseudo (en-XA)'
}

/**
 * Locales offered to users in production builds (picker + device-language detection).
 * A locale is added here once its wave has been machine-translated AND the
 * security-critical keys were reviewed by a native speaker (see
 * docs/i18n-multi-language-plan.md, Phases 5–7). Dev builds offer every locale.
 */
export const RELEASED_LOCALES: Locale[] = [
  Locale.EN,
  Locale.KO,
  Locale.JA,
  Locale.ZH_CN,
  Locale.ZH_TW,
  Locale.RU,
  Locale.PL,
  Locale.CS,
  Locale.FR,
  Locale.DE,
  Locale.NL
]

export const isDevBuild = process.env.APP_ENV === 'development'

export const getAvailableLocales = (): AppLocale[] =>
  isDevBuild ? [...Object.values(Locale), PSEUDO_LOCALE] : RELEASED_LOCALES

export const isAppLocale = (value: unknown): value is AppLocale =>
  typeof value === 'string' &&
  ((Object.values(Locale) as string[]).includes(value) || value === PSEUDO_LOCALE)

/**
 * Marks a user-visible string that is stored (constants, config maps) and
 * translated later with `t(value)`. Returns the key unchanged; its only job is to
 * let `yarn i18n:extract` find the key.
 */
export const i18nKey = <T extends string>(
  key: T,
  // `{ count }` marks a plural key for `yarn i18n:extract`; not used at runtime.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _options?: { count: number }
): T => key
